// Pure builders that turn a hydrated OutputFormula (from the picker's change
// event) plus the picker's cached FileSpecification + FilenameConvention name
// lookups into the OutputFormulaReference payload the BE gen-outputs handler
// requires (ADR 009). Fields the BE marks `required` on OutputFormulaReference
// / OutputFormulaSpecSnapshot / OutputFormulaConventionSnapshot are populated
// here so JSON deserialisation succeeds. Extracted from
// create-outputs.component.ts so the per-variant snapshot mapping is unit-
// testable in isolation from Angular DI.
//
// Media-tag mapping (structurally per-variant):
//   rangeStills / evStills → single Stills spec snapshot
//   rangeVideos / evVideos → single Videos spec snapshot
//   rangeBoth / evBoth     → Stills snapshot + Videos snapshot (in that order)
//
// Convention-snapshot mapping (mirrors OutputFormulaReferenceSnapshotBuilder.cs):
//   rangeStills → stills-side conventionId
//   rangeVideos → videos-side conventionId
//   rangeBoth   → stills-side conventionId (top-level slot semantics)
//   ev*         → FIRST id in evConventionIds (first-match-wins mirrors
//                 resolve-time semantics)
//
// Revision-ordinal note: outputFormulaRevisionOrdinal is a BE-computed field
// (row count per logical entity) not exposed on the FE OutputFormula surface.
// We send 0 as a sentinel — the gen-outputs handler does not consume this
// field at runtime, only requires it for deserialisation. If a future BE
// consumer starts consuming it, expose it via the list endpoint and thread it
// through here.

import { isDevMode } from '@angular/core';
import { OutputFormula } from 'src/app/models/output-formulas/output-formula';
import { OutputFormulaValue } from 'src/app/models/output-formulas/output-formula-value';
import { OutputFormulaConventionSnapshot } from 'src/app/models/outputs-collections/shared/output-formula-convention-snapshot';
import { OutputFormulaReference } from 'src/app/models/outputs-collections/shared/output-formula-reference';
import {
  OutputFormulaSpecSnapshot,
  OutputFormulaSpecSnapshotMedia,
} from 'src/app/models/outputs-collections/shared/output-formula-spec-snapshot';

export interface FormulaSnapshotLookups {
  readonly specNamesById: ReadonlyMap<string, string>;
  readonly conventionNamesById: ReadonlyMap<string, string>;
}

const REVISION_ORDINAL_SENTINEL = 0;

export const buildFormulaReference = (
  formula: OutputFormula,
  lookups: FormulaSnapshotLookups,
): OutputFormulaReference => {
  if (isDevMode()) {
    console.log('[buildFormulaReference]', {
      formulaId: formula.id,
      formulaName: formula.name,
      value: formula.value,
      valueKind: formula.value?.kind,
      valueKeys: formula.value ? Object.keys(formula.value) : null,
    });
  }
  return {
    outputFormulaId: formula.id,
    outputFormulaName: formula.name,
    outputFormulaRevisionOrdinal: REVISION_ORDINAL_SENTINEL,
    fileSpecs: buildSpecSnapshots(formula.value, lookups.specNamesById),
    filenameConvention: buildConventionSnapshot(formula.value, lookups.conventionNamesById),
  };
};

export const buildFormulaReferences = (
  formulas: readonly OutputFormula[],
  lookups: FormulaSnapshotLookups,
): OutputFormulaReference[] =>
  formulas.map((formula) => buildFormulaReference(formula, lookups));

const buildSpecSnapshots = (
  value: OutputFormulaValue,
  specNamesById: ReadonlyMap<string, string>,
): OutputFormulaSpecSnapshot[] => {
  switch (value.kind) {
    case 'rangeStills':
      return [specSnapshot(value.stills.specId, 'Stills', specNamesById)];
    case 'rangeVideos':
      return [specSnapshot(value.videos.specId, 'Videos', specNamesById)];
    case 'rangeBoth':
      return [
        specSnapshot(value.stills.specId, 'Stills', specNamesById),
        specSnapshot(value.videos.specId, 'Videos', specNamesById),
      ];
    case 'evStills':
      return [specSnapshot(value.stillsSpecId, 'Stills', specNamesById)];
    case 'evVideos':
      return [specSnapshot(value.videosSpecId, 'Videos', specNamesById)];
    case 'evBoth':
      return [
        specSnapshot(value.stillsSpecId, 'Stills', specNamesById),
        specSnapshot(value.videosSpecId, 'Videos', specNamesById),
      ];
  }
};

const specSnapshot = (
  specId: string,
  media: OutputFormulaSpecSnapshotMedia,
  specNamesById: ReadonlyMap<string, string>,
): OutputFormulaSpecSnapshot => ({
  fileSpecificationId: specId,
  name: specNamesById.get(specId) ?? '',
  media,
});

const buildConventionSnapshot = (
  value: OutputFormulaValue,
  conventionNamesById: ReadonlyMap<string, string>,
): OutputFormulaConventionSnapshot => {
  const conventionId = pickConventionId(value);
  return {
    filenameConventionId: conventionId,
    name: conventionNamesById.get(conventionId) ?? '',
  };
};

const pickConventionId = (value: OutputFormulaValue): string => {
  switch (value.kind) {
    case 'rangeStills':
      return value.stills.conventionId;
    case 'rangeVideos':
      return value.videos.conventionId;
    case 'rangeBoth':
      return value.stills.conventionId;
    case 'evStills':
    case 'evVideos':
    case 'evBoth':
      return value.evConventionIds[0] ?? '';
  }
};
